import { Link } from '@tanstack/react-router'
import {
  BadgeCheck,
  Bot,
  Boxes,
  Brain,
  Search,
  ShoppingCart,
} from 'lucide-react'
import {
  BENCHMARKS,
  GetStartedButton,
  LOCATION_COUNT,
} from '@/components/marketing/shared'
import { Button } from '@/components/ui/button'
import { stickyWindowShort } from '@/lib/credentials/credentials'

interface MicroUseCase {
  label: string
  /** Blog slug once the article exists; unlinked chips render as spans. */
  slug?: string
}

export const useCases = [
  {
    id: 'ai-data',
    tab: 'AI data',
    icon: Brain,
    title: 'Training and retrieval data from the real web',
    body: 'Build training corpora, fine-tuning sets, and live RAG context from US mobile IPs that get served the same page a real reader sees, not a bot-blocked stub. Metering is byte for byte, so a 200 GB crawl costs exactly 200 GB.',
    micros: [
      {
        label: 'LLM training corpora',
        slug: 'mobile-proxies-for-ai-training-data',
      },
      { label: 'RAG retrieval' },
      { label: 'Eval and grounding data' },
    ] as MicroUseCase[],
    stats: [
      { value: '1:1', label: 'billed byte for byte, no rounding' },
      { value: String(LOCATION_COUNT), label: 'US cities to pull from' },
    ],
  },
  {
    id: 'ai-agents',
    tab: 'AI agents',
    icon: Bot,
    title: 'Give your agent a real place to browse from',
    body: 'Point an autonomous agent at the gateway and it loads pages the way a person on a phone would, past the blocks that stop a datacenter IP cold. Rotate per request to cover more ground, or hold one sticky IP through a multi-step task.',
    micros: [
      { label: 'Agent tool use', slug: 'proxies-for-ai-agents' },
      { label: 'Automated research' },
      { label: 'Web navigation' },
    ] as MicroUseCase[],
    stats: [
      {
        value: '4',
        label: 'protocols per credential: HTTP, HTTPS, SOCKS5, HTTP/3',
      },
      { value: stickyWindowShort(), label: 'hold one IP per task' },
    ],
  },
  {
    id: 'scraping',
    tab: 'Web scraping',
    icon: Boxes,
    title: 'Crawlers that run without babysitting',
    body: 'Rotate to a fresh mobile IP on every request or pin one through a login flow, from Scrapy, Playwright, or plain HTTP. Every credential reports its own spend live, and jobs pause at zero instead of running up a bill.',
    micros: [
      { label: 'Price monitoring' },
      { label: 'Product catalogs' },
      {
        label: 'Rotating vs sticky sessions',
        slug: 'rotating-vs-sticky-sessions',
      },
    ] as MicroUseCase[],
    stats: [
      { value: BENCHMARKS.roundTrip, label: 'average connection time' },
      { value: BENCHMARKS.successRate, label: 'request success rate' },
    ],
  },
  {
    id: 'ads',
    tab: 'Ad verification',
    icon: BadgeCheck,
    title: 'See your ads the way a real subscriber does',
    body: 'Load placements from real carrier IPs in the exact city a campaign targets, and confirm the creative, the landing page, and the geo all match the insertion order before the client does.',
    micros: [
      { label: 'Geo-targeted creatives' },
      {
        label: 'Landing page checks',
        slug: 'ad-verification-mobile-proxies',
      },
      { label: 'Affiliate policing' },
    ] as MicroUseCase[],
    stats: [
      { value: String(LOCATION_COUNT), label: 'US metro areas to check from' },
      { value: '1', label: 'username suffix pins a city' },
    ],
  },
  {
    id: 'serp',
    tab: 'Rank tracking',
    icon: Search,
    title: 'Track rankings where they actually differ',
    body: 'There is no single results page. Search results, map packs, and prices shift by metro and by network type, so collect them from mobile IPs in each market you compete in, on a schedule.',
    micros: [
      {
        label: 'Local rank tracking',
        slug: 'local-rank-tracking-mobile-proxies',
      },
      { label: 'Map packs' },
      { label: 'Price feeds' },
    ] as MicroUseCase[],
    stats: [
      { value: String(LOCATION_COUNT), label: 'US metro areas covered' },
      { value: '24/7', label: 'collection, jobs never sleep' },
    ],
  },
  {
    id: 'research',
    tab: 'Price research',
    icon: ShoppingCart,
    title: 'Compare prices and fares that change by location',
    body: 'Flights, hotels, and retail prices get quoted differently depending on where the request comes from. Check them from a real mobile connection in each city instead of guessing what a shopper somewhere else is shown.',
    micros: [
      {
        label: 'Comparison shopping',
        slug: 'why-prices-change-by-location',
      },
      { label: 'Travel fares' },
      { label: 'Regional stock checks' },
    ] as MicroUseCase[],
    stats: [
      { value: String(LOCATION_COUNT), label: 'US cities to compare from' },
      { value: '$0', label: 'surprise overages, data is prepaid' },
    ],
  },
]

/** Subtle dark background motifs, cycled per panel so adjacent cards read as
 *  distinct surfaces rather than copies of each other. White-on-navy only. */
const MOTIFS = [
  // grid
  '[background-image:linear-gradient(to_right,rgb(255_255_255/0.045)_1px,transparent_1px),linear-gradient(to_bottom,rgb(255_255_255/0.045)_1px,transparent_1px)] [background-size:32px_32px]',
  // dots
  '[background-image:radial-gradient(rgb(255_255_255/0.07)_1px,transparent_1.6px)] [background-size:22px_22px]',
  // diagonal hatch
  '[background-image:repeating-linear-gradient(45deg,rgb(255_255_255/0.05)_0,rgb(255_255_255/0.05)_1px,transparent_1px,transparent_13px)]',
  // concentric rings
  '[background-image:radial-gradient(circle,transparent_0,transparent_37px,rgb(255_255_255/0.06)_38px,transparent_39px)] [background-size:78px_78px]',
]

/** One use-case panel: a full navy card whose background motif and (on wide
 *  screens) left/right-flipped layout set it apart from its neighbors. All
 *  panels render in the DOM (no tabs or carousel) so every word is crawlable;
 *  the floating pill nav jumps between them. */
function UseCasePanel({
  u,
  index,
}: {
  u: (typeof useCases)[number]
  index: number
}) {
  const flip = index % 2 === 1
  const motif = MOTIFS[index % MOTIFS.length]
  return (
    <div
      id={u.id}
      className="relative scroll-mt-24 overflow-hidden rounded-2xl bg-(--neutral-900) text-(--neutral-50)"
    >
      <div
        aria-hidden
        className={`pointer-events-none absolute inset-0 ${motif}`}
      />
      <div
        aria-hidden
        className={`pointer-events-none absolute size-[26rem] rounded-full bg-primary opacity-20 blur-3xl ${
          flip ? 'bottom-[-30%] left-[-6%]' : '-top-28 right-[-6%]'
        }`}
      />
      <div
        className={`relative grid gap-10 px-8 py-10 sm:px-12 lg:gap-14 lg:px-14 ${
          flip ? 'lg:grid-cols-[1fr_1.6fr]' : 'lg:grid-cols-[1.6fr_1fr]'
        }`}
      >
        <div
          className={`flex flex-col items-start gap-5 ${flip ? 'lg:order-2' : ''}`}
        >
          <h3 className="text-3xl font-semibold tracking-tight text-balance sm:text-4xl">
            {u.title}
          </h3>
          <p className="max-w-xl text-lg text-(--neutral-200) text-pretty">
            {u.body}
          </p>
          <div className="flex flex-wrap gap-2 pt-1">
            {u.micros.map((m) => (
              <span
                key={m.label}
                className="rounded-full border border-white/15 bg-white/5 px-3 py-1 text-sm text-(--neutral-100)"
              >
                {m.label}
              </span>
            ))}
          </div>
        </div>

        <div
          className={`flex flex-col justify-center gap-8 border-white/10 ${
            flip ? 'lg:order-1 lg:border-r lg:pr-12' : 'lg:border-l lg:pl-12'
          }`}
        >
          {u.stats.map((s) => (
            <div key={s.label} className="flex flex-col gap-1">
              <span className="text-5xl font-bold tracking-tight text-primary tabular-nums">
                {s.value}
              </span>
              <span className="text-sm text-(--neutral-300) text-pretty">
                {s.label}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function UseCases() {
  return (
    <section id="use-cases" className="scroll-mt-20 border-t">
      <div className="mx-auto flex w-full max-w-6xl flex-col gap-8 px-6 py-20">
        <div className="mx-auto flex max-w-2xl flex-col gap-4 text-center">
          <h2 className="text-3xl font-bold tracking-tight text-balance sm:text-4xl">
            One gateway, every use case
          </h2>
          <p className="text-lg text-muted-foreground text-pretty">
            From AI training data to price research, teams and solo builders run
            the same real US mobile IPs behind whatever they are building.
          </p>
        </div>
        {useCases.map((u, index) => (
          <UseCasePanel key={u.id} u={u} index={index} />
        ))}
        <div className="mt-4 flex flex-wrap items-center justify-center gap-3">
          <GetStartedButton />
          <Button asChild variant="ghost">
            <Link to="/docs">Read the docs</Link>
          </Button>
        </div>
      </div>
    </section>
  )
}
